import { useCallback, useState } from 'react';
import { Outlet, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { NavRail } from '@/components/nav/NavRail';
import { TENANTS, type TenantKey } from '@/components/nav/nav-config';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useRouteTitle } from '@/hooks/useRouteTitle';
import { usePushLifecycle } from '@/hooks/usePushLifecycle';

export function AppLayout() {
  const bp = useBreakpoint();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentTenant] = useState<TenantKey>('P');
  const { t } = useTranslation();
  const title = useRouteTitle();
  const tenant = TENANTS.find((x) => x.key === currentTenant) ?? TENANTS[1];

  const navigate = useNavigate();
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const openDrawer = useCallback(() => setDrawerOpen(true), []);

  // Wire FCM/APNS to auth lifecycle. Native-only; safe no-op on web/dev.
  usePushLifecycle();

  const isDesktop = bp === 'desktop';

  return (
    <div className="app-layout" data-bp={bp}>
      {isDesktop && <NavRail mode="full" currentTenant={currentTenant} />}

      {bp === 'phone' && drawerOpen && (
        <>
          <div
            className="phone-drawer-scrim"
            role="presentation"
            onClick={closeDrawer}
          />
          <aside className="phone-drawer">
            <NavRail
              mode="full"
              currentTenant={currentTenant}
              onNavigate={closeDrawer}
            />
          </aside>
        </>
      )}

      <div className="content-area">
        <header className="top-bar">
          {bp === 'phone' && (
            <button
              type="button"
              className="top-bar-menu"
              onClick={openDrawer}
              aria-label={t('nav.switch_tenant')}
            >
              <span className="material-symbols-outlined">menu</span>
            </button>
          )}
          <div className="top-bar-title">
            {title}
            <span className="top-bar-title-sub">· {t(tenant.nameKey)}</span>
          </div>
          <button type="button" className="top-bar-action" aria-label="Search">
            <span className="material-symbols-outlined">search</span>
          </button>
          <button
            type="button"
            className="top-bar-action"
            aria-label={t('nav.notifications')}
            onClick={() => navigate('/notices')}
          >
            <span className="material-symbols-outlined">notifications</span>
            <span className="top-bar-action-dot" />
          </button>
        </header>
        <div className="content-scroll">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
